import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Switch, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SettingIcon from '../../_icons/SettingIcon';
import AreaSpotSettingModal from './StoreModals/AreaSpotSettingModal';
import { getGolfAreaSpotDetailById } from '../../api/golf';
import { useSelector } from 'react-redux';

const GolfSpotSettingWidget = ({ box, handleCheckSpotSetting, refreshSpotAreas }) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [openSpotAreaSettingModal, setOpenSpotAreaSettingModal] = useState(false);
  const user = useSelector(state => state.user.user);
  const isChecked = box.spotStatus === 'READY';
  const [widgetData, setWidgetData] = useState(null);

  useEffect(() => {
    if (!openSpotAreaSettingModal || !user?.token || !box?.id) {
      return;
    }
    let isMounted = true;
    const fetchSpotDetail = async () => {
      try {
        const response = await getGolfAreaSpotDetailById(user.token, box.id);
        if (isMounted) {
          setWidgetData(response);
        }
      } catch (error) {
        if (isMounted) {
          console.log('골프 스팟 상세 정보 조회 실패', error);
        }
      }
    };
    fetchSpotDetail();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSpotAreaSettingModal]);

  const handleSpotAreaSettingModalOpen = useCallback(e => {
    e?.stopPropagation();
    setOpenSpotAreaSettingModal(true);
  }, []);

  const handleSpotAreaSettingModalClose = useCallback(() => {
    setOpenSpotAreaSettingModal(false);
    setWidgetData(null);
  }, []);

  const handleCreateSpotArea = () => {
    console.log('handleCreateSpotArea');
  };

  return (
    <>
      <Box
        key={box.id}
        sx={{
          width: '160px',
          height: '164px',
          aspectRatio: '1',
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: box.spotStatus === 'DISABLED' ? '#A6A6A6' : '#ffffff',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          p: '12px',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {isHovered && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              borderRadius: '16px',
              zIndex: 1,
              pointerEvents: 'none',
            }}
          />
        )}
        <Box
          sx={{
            width: '100%',
            height: '32px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            position: 'relative',
            zIndex: 2,
          }}>
          <Typography sx={{ fontSize: '16px', color: '#087F85', width: '90px', fontWeight: '600' }} component="span">
            {box.spotName}
          </Typography>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              display: 'flex',
              mt: '-7px',
            }}>
            {box.spotStatus !== 'DISABLED' && (
              <Switch
                sx={{ transform: 'scale(0.9)' }}
                checked={isChecked}
                onChange={() => {
                  const newStatus = isChecked ? 'SUSPENDED' : 'READY';
                  handleCheckSpotSetting(newStatus);
                }}
              />
            )}
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: '22px',
          }}>
          <Box
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleSpotAreaSettingModalOpen}
            sx={{
              width: '72px',
              height: '32px',
              border: box.spotStatus === 'DISABLED' ? '1px solid #FFFFFF' : '1px solid #A6A6A6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              gap: '4px',
              cursor: 'pointer',
              position: 'relative',
              zIndex: 3,
              '&:hover': {
                border: '1px solid #FFFFFF',
                '& .setting-text': {
                  color: '#FFFFFF',
                },
              },
            }}>
            <SettingIcon
              color={box.spotStatus === 'DISABLED' ? '#FFFFFF' : isHovered ? '#FFFFFF' : '#A6A6A6'}
              width={16}
              height={16}
            />
            <Typography
              className="setting-text"
              sx={{
                fontSize: '16px',
                color: box.spotStatus === 'DISABLED' ? '#FFFFFF' : '#A6A6A6',
              }}>
              {t('Setting')}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            width: '136px',
            height: '32px',
            flexDirection: 'row',
            mt: '22px',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
          }}>
          {/* 앱 예약 허용 스팟에만 배지. 불가(N)인데도 항상 떠 있던 문제 */}
          {box.appReservationEnable === 'Y' && (
            <Button
              sx={{
                width: '56px',
                height: '22px',
                background: '#0F7F85',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#FFFFFF',
                '&:hover': {
                  background: '#ffffff',
                  border: '1px solid #0F7F85',
                  color: '#0F7F85',
                },
              }}>
              {t('App reservation')}
            </Button>
          )}
          {box.spotStatus === 'READY' && (
            <Button
              sx={{
                width: '56px',
                height: '22px',
                border: '1px solid #0F7F85',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#0F7F85',
              }}>
              {t('In use')}
            </Button>
          )}
          {box.spotStatus === 'SUSPENDED' && (
            <Button
              sx={{
                width: '56px',
                height: '22px',
                border: '1px solid #D9D9D9',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#D9D9D9',
              }}>
              {t('Waiting for use')}
            </Button>
          )}
          {box.spotStatus === 'DISABLED' && (
            <Button
              sx={{
                width: '56px',
                height: '22px',
                border: '1px solid #D9D9D9',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#D9D9D9',
              }}>
              {t('Unusable')}
            </Button>
          )}
        </Box>
      </Box>
      <AreaSpotSettingModal
        open={openSpotAreaSettingModal}
        handleClose={handleSpotAreaSettingModalClose}
        onCreate={handleCreateSpotArea}
        widgetData={widgetData}
        refreshSpotAreas={refreshSpotAreas}
      />
    </>
  );
};

export default GolfSpotSettingWidget;
